<template>
    <div class="shelf-container">
        <img
            src="/regal_apo-leer-new.png"
            alt="Pharmacy Shelf"
            class="shelf-background"
        />

        <div
            v-for="shelfNum in 8"
            :key="`shelf-${shelfNum}`"
            class="shelf-zone"
            :class="[`shelf-${shelfNum}`, { highlight: shouldHighlight(shelfNum) }]"
            @drop="handleDrop($event, shelfNum)"
            @dragover.prevent
            @dragenter="handleDragEnter($event, shelfNum)"
            @dragleave="handleDragLeave"
        >
            <div class="shelf-content">
                <img
                    v-for="(product, idx) in getShelfProducts(shelfNum)"
                    :key="`${product.id}-${product.index}`"
                    :src="product.image"
                    :alt="product.name"
                    :class="['shelf-product', `product-${product.id}`]"
                    :style="{ animationDelay: `${idx * 0.08}s` }"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { PlacedProduct } from '../../types/quiz';

    const props = defineProps<{
        placedProducts: Record<number, PlacedProduct[]>;
        correctShelf: number;
        draggedProduct: string | null;
    }>();

    const emit = defineEmits<{
        drop: [shelfNum: number, event: DragEvent];
        dragEnter: [shelfNum: number];
        dragLeave: [];
    }>();

    const shouldHighlight = (shelfNum: number) => {
        return shelfNum === props.correctShelf;
    };

    const getShelfProducts = (shelfNum: number) => {
        return props.placedProducts[shelfNum] || [];
    };

    const handleDrop = (event: DragEvent, shelfNum: number) => {
        emit('drop', shelfNum, event);
    };

    const handleDragEnter = (event: DragEvent, shelfNum: number) => {
        event.preventDefault();
        emit('dragEnter', shelfNum);
    };

    const handleDragLeave = () => {
        emit('dragLeave');
    };
</script>

<style scoped>
    .shelf-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
    }

    .shelf-background {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
    }

    .shelf-zone {
        position: absolute;
        width: 65%;
        left: 50%;
        transform: translate(-50%);
        z-index: 10;
        height: 4.5%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid rgba(26, 155, 189, 0.3);
        border-radius: 0;
        background: #ffffff0d;
        transition: all 0.3s ease;
    }

    .shelf-zone.highlight {
        background: rgba(244, 151, 25, 0.2);
        border: 3px solid #f49719;
        border-style: dashed;
    }

    .shelf-1 {
        top: 17.7%;
    }

    .shelf-2 {
        top: 22.8%;
    }

    .shelf-3 {
        top: 27.9%;
    }

    .shelf-4 {
        top: 32.9%;
    }

    .shelf-5 {
        top: 37.9%;
    }

    .shelf-6 {
        top: 43.2%;
    }

    .shelf-7 {
        top: 48.5%;
    }

    .shelf-8 {
        top: 54.2%;
    }

    .shelf-content {
        display: flex;
        gap: 8px;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
    }

    .shelf-product {
        width: auto;
        height: 85%;
        object-fit: contain;
        animation: dropIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    }

    .shelf-product.product-medigel {
        height: 72%;
    }

    .shelf-product.product-soventol {
        height: 72%;
    }

    @keyframes dropIn {
        0% {
            opacity: 0;
            transform: translateY(-40%);
        }

        50% {
            opacity: 1;
        }

        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }
</style>
